import Tooltip from '@/ui/Tooltip'
import { PAGE_BG, pctX, pctY } from '../timelineDiagramGeometry'

// A clickable label floating over the SVG at diagram coordinates. `anchor`
// controls which edge of the label sits at (x, y) so labels hug their line.
//   • `overlay` paints the page background behind the label (for labels that
//     ride directly on a line).
//   • `emphasis` renders the label bold + larger (the spine plan's highlight,
//     in place of a filled background).
//   • `tooltip` shows a hover tooltip with the gist of what the label points at.
type DiagramLabelProps = {
  x: number
  y: number
  anchor: 'start' | 'end' | 'center'
  active?: boolean
  overlay?: boolean
  emphasis?: boolean
  tooltip?: React.ReactNode
  onClick: () => void
  children: React.ReactNode
}

export const DiagramLabel = ({
  x,
  y,
  anchor,
  active,
  overlay,
  emphasis,
  tooltip,
  onClick,
  children,
}: DiagramLabelProps) => {
  const translateX = anchor === 'end' ? '-100%' : anchor === 'center' ? '-50%' : '0'

  const button = (
    <button
      type="button"
      onClick={onClick}
      className={`group absolute whitespace-nowrap rounded-md px-3 py-1.5 font-mono leading-tight ${
        emphasis ? 'text-[16px] font-bold tracking-tight' : 'text-[12px] font-medium'
      } ${active ? 'text-black' : 'text-black/80 hover:text-black hover:bg-black/[0.05]'}`}
      style={{
        left: pctX(x),
        top: pctY(y),
        transform: `translate(${translateX}, -50%)`,
        backgroundColor: overlay ? PAGE_BG : undefined,
        // text-color stays snappy for hover; the overlay's page background fades
        // over the same period AND easing as the page itself (see Playbook) so an
        // on-line label doesn't lag the backdrop as the epilogue dims.
        transition:
          'color 150ms ease, background-color var(--playbook-epilogue-bg-fade-ms, 333ms) cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {children}
    </button>
  )

  if (!tooltip) return button

  return (
    <Tooltip content={<div className="font-sans text-sm">
      <div className="font-mono text-[12px] font-bold mb-2 uppercase">{children}</div>
      {tooltip}
    </div>} placement="top-start" maxWidth={260}>
      {button}
    </Tooltip>
  )
}
